<template>
  <el-dialog
    v-model="visible"
    width="560px"
    class="enterprise-upgrade-confirm"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :title="upgrade ? $t('enterprise_ui_confirm_upgrade_plan_title') : $t('enterprise_ui_confirm_override_local_plan_title')"
    align-center
    append-to-body
    @closed="resolveIfPending(false)"
  >
    <div class="override-warning">
      <div class="override-warning__title"><span class="override-warning__icon" aria-hidden="true" />{{ $t('enterprise_ui_irreversible_action') }}</div>
      <p v-if="upgrade">{{ upgradeWarningText }}</p>
      <p v-else>{{ $t('enterprise_ui_local_override_warning') }}</p>
    </div>

    <dl class="entitlement-summary">
      <div>
        <dt>{{ $t('enterprise_ui_original_plan') }}</dt>
        <dd>{{ serviceLevelName }}</dd>
      </div>
      <div>
        <dt>{{ $t('enterprise_ui_remaining_chars') }}</dt>
        <dd>{{ formatCharacters(remainingQuota) }}</dd>
      </div>
      <div>
        <dt>{{ $t('enterprise_ui_expiration_time') }}</dt>
        <dd>{{ formatDate(endTime) }}</dd>
      </div>
    </dl>
    <p v-if="upgrade" class="upgrade-target"><span>{{ $t('enterprise_ui_upgrade_to') }}</span><strong>{{ upgrade.targetName }}</strong></p>

    <el-checkbox v-model="confirmed" class="override-checkbox">
      {{ $t('enterprise_ui_confirm_original_plan_replaced') }}
    </el-checkbox>

    <template #footer>
      <el-button class="upgrade-cancel" @click="cancel">{{ $t('enterprise_ui_cancel_purchase') }}</el-button>
      <el-button type="primary" :disabled="!confirmed" @click="accept">{{ $t('enterprise_ui_confirm_continue_pay') }}</el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import type { GlobalBizEligibility } from '~/services/GlobalBizServer/types'
import type { EnterprisePlan } from '~/utils/enterpriseTrade'

interface UpgradeConfirmation {
  targetName: string
  targetGlobal: boolean
  currentPlan: EnterprisePlan | null
  remainingQuota?: number
  endTime?: string
}

const visible = ref(false)
const confirmed = ref(false)
const data = ref<GlobalBizEligibility | null>(null)
const upgrade = ref<UpgradeConfirmation | null>(null)
const remainingQuota = computed(() => upgrade.value?.remainingQuota ?? data.value?.localRemainingQuota)
const endTime = computed(() => upgrade.value?.endTime ?? data.value?.localEndTime)
let resolver: ((value: boolean) => void) | null = null
const { t } = useI18n()
const upgradeWarningText = computed(() => t('enterprise_ui_upgrade_override_warning', {
  trigger: upgrade.value?.targetGlobal
    ? t('enterprise_ui_after_allocation_and_activation')
    : t('enterprise_ui_after_payment_success'),
}))

const serviceLevelName = computed(() => {
  const names: Record<number, string> = {
    1: t('enterprise_ui_plan_base'),
    2: t('enterprise_ui_plan_professional'),
    3: t('enterprise_ui_plan_advanced'),
  }
  const currentPlan = upgrade.value?.currentPlan
  if (currentPlan) {
    return currentPlan.mode === 'global'
      ? t('enterprise_ui_global_plan_name', { name: names[currentPlan.rank] || t('enterprise_ui_plan_custom') })
      : (names[currentPlan.rank] || t('enterprise_ui_plan_custom'))
  }
  return names[data.value?.localServiceLevel || 0]
    || t('enterprise_ui_local_plan_level', { level: data.value?.localServiceLevel || '-' })
})

function open(eligibility: GlobalBizEligibility, confirmation?: UpgradeConfirmation): Promise<boolean> {
  resolveIfPending(false)
  data.value = eligibility
  upgrade.value = confirmation || null
  confirmed.value = false
  visible.value = true
  return new Promise<boolean>((resolve) => {
    resolver = resolve
  })
}

onBeforeUnmount(() => resolveIfPending(false))

function accept() {
  if (!confirmed.value) return
  visible.value = false
  resolveIfPending(true)
}

function cancel() {
  visible.value = false
  resolveIfPending(false)
}

function resolveIfPending(value: boolean) {
  if (!resolver) return
  const currentResolver = resolver
  resolver = null
  currentResolver(value)
}

function formatCharacters(value?: number) {
  return t('enterprise_ui_chars_value', { count: Number(value || 0).toLocaleString() })
}

function formatDate(value?: string) {
  if (!value) return '-'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

defineExpose({ open })
</script>

<style scoped lang="scss">
:global(.el-dialog.enterprise-upgrade-confirm) {
  // main.scss resets every dialog's width, padding and header with !important.
  // Override only this dialog; other purchase dialogs keep their existing layout.
  width: 560px !important;
  max-width: calc(100vw - 32px);
  box-sizing: border-box;
  padding: 26px !important;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 16px 44px #0a234d33;
  --el-color-primary: #2b7de9;
}

:global(.el-dialog.enterprise-upgrade-confirm .el-dialog__header) {
  height: auto !important;
  min-height: 24px;
  margin: 0 0 18px !important;
  padding: 0 32px 0 0 !important;
}

:global(.enterprise-upgrade-confirm .el-dialog__title) {
  color: #1f2329;
  font-size: 18px;
  font-weight: 800;
  line-height: 24px;
}

:global(.enterprise-upgrade-confirm .el-dialog__headerbtn) {
  top: 24px;
  right: 24px;
  width: 24px;
  height: 24px;
}

:global(.enterprise-upgrade-confirm .el-dialog__headerbtn .el-dialog__close) {
  color: #8f959e;
  font-size: 20px;
}

:global(.enterprise-upgrade-confirm .el-dialog__body) {
  padding: 0;
  color: #51565d;
  font-size: 13px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

:global(.enterprise-upgrade-confirm .el-dialog__footer) {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 18px 0 0;
}

:global(.enterprise-upgrade-confirm .el-dialog__footer .el-button) {
  margin: 0;
  min-width: 0;
  width: 100%;
  height: 44px;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 700;
  border-radius: 10px;
}

:global(.enterprise-upgrade-confirm .upgrade-cancel) {
  color: #2b7de9;
  border-color: #2b7de9;
  background: #fff;
}

:global(.enterprise-upgrade-confirm .el-dialog__footer .el-button--primary) {
  color: #fff;
  border-color: #2b7de9;
  background: #2b7de9;
}

:global(.enterprise-upgrade-confirm .el-dialog__footer .el-button--primary.is-disabled) {
  color: #a8b0ba;
  border-color: #edeff2;
  background: #edeff2;
  cursor: not-allowed;
}

.override-warning {
  padding: 14px 16px;
  color: #92400e;
  background: #fff7e6;
  border: 1px solid #fde68a;
  border-radius: 10px;

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 13px;
    font-weight: 700;
  }

  &__icon {
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    background: #d97706;
    mask: url('/assets/images/workbench/icons/alert-triangle.svg') center / contain no-repeat;
  }

  p {
    margin: 0;
    line-height: 1.6;
  }
}

.entitlement-summary {
  display: grid;
  grid-template-columns: 1fr 1fr 1.4fr;
  gap: 10px;
  margin: 16px 0;

  div {
    min-width: 0;
    padding: 12px;
    background: #f7f9fc;
    border-radius: 8px;
  }

  dt {
    margin-bottom: 6px;
    color: #8f959e;
    font-size: 12px;
  }

  dd {
    margin: 0;
    color: #1f2329;
    font-size: 13px;
    font-weight: 600;
  }
}

.override-checkbox {
  display: flex;
  align-items: flex-start;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 14px 16px;
  border: 1px solid #e3e7ec;
  border-radius: 10px;
  background: #f7f9fc;
  white-space: normal;
  height: auto;
  :deep(.el-checkbox__input) {
    flex: 0 0 auto;
    margin-top: 3px;
  }
  :deep(.el-checkbox__label) {
    min-width: 0;
    padding-left: 10px;
    color: #1f2329;
    font-size: 12.5px;
    font-weight: 600;
    white-space: normal;
    line-height: 1.6;
  }
}

.upgrade-target {
  display: flex;
  gap: 12px;
  align-items: baseline;
  margin: 0 0 16px;
  color: #51565d;

  strong {
    min-width: 0;
    color: #2b7de9;
    font-size: 14px;
    font-weight: 700;
  }
}

@media (max-width: 480px) {
  :global(.el-dialog.enterprise-upgrade-confirm) {
    padding: 20px !important;
  }

  :global(.enterprise-upgrade-confirm .el-dialog__headerbtn) {
    top: 20px;
    right: 20px;
  }

  .entitlement-summary {
    grid-template-columns: 1fr;

    div {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 12px;
    }

    dt { flex: 0 0 auto; margin: 0; }
    dd { text-align: right; }
  }
}
</style>
